import { useSelector } from "react-redux";
import { skipToken } from "@reduxjs/toolkit/query";

import { selectProjectAccount, useGetVersionBomQuery, useGetVersionStatusQuery } from "fond/api";
import { useCanViewBom } from "fond/hooks/useCanViewBom";
import { useGetScenarioConfiguration } from "fond/hooks/useGetScenarioConfiguration";
import { BomOutput, Project, Store } from "fond/types";
import { isCollaborationProject, isPlannerProject } from "fond/utils/project";

export type BomStatus =
  | { kind: "planner-running" }
  | { kind: "planner-no-bom" }
  | { kind: "unavailable" }
  | { kind: "loading" }
  | { kind: "no-bom" }
  | { kind: "ready"; bom: BomOutput };

interface UseInteractiveBomStatusArgs {
  project: Project;
  versionId: string;
}

interface UseInteractiveBomStatusReturn {
  status: BomStatus;
  bomArchRestrictions: unknown;
}

/**
 * Returns the status of the interactive BOM for a given project and version.
 */
export function useInteractiveBomStatus({ project, versionId }: UseInteractiveBomStatusArgs): UseInteractiveBomStatusReturn {
  const canViewBom = useCanViewBom(project);
  const isCollaboration = isCollaborationProject(project);
  const bomArchRestrictions = useSelector((state: Store) => selectProjectAccount(state, project)?.BomArchRestrictions);

  const { hasConfig, isLoading: isLoadingConfig } = useGetScenarioConfiguration({ projectId: isCollaboration ? project.ID : undefined });
  const { data: versionStatus } = useGetVersionStatusQuery(versionId, { skip: isCollaboration || !versionId });

  const skipBom = !versionId || (isCollaboration && !hasConfig);
  const { data: bom, isLoading: isLoadingBom, isError: isBomError } = useGetVersionBomQuery(skipBom ? skipToken : versionId);

  const status: BomStatus = (() => {
    if (isPlannerProject(project)) {
      if (versionStatus?.Status === "Running") return { kind: "planner-running" };
      if (isLoadingBom) return { kind: "loading" };
      if (isBomError || !bom) return { kind: "planner-no-bom" };
      return { kind: "ready", bom };
    } else if (isCollaboration) {
      if (isLoadingConfig) return { kind: "loading" };
      if (!hasConfig) return { kind: "unavailable" };
    } else if (!canViewBom) {
      return { kind: "unavailable" };
    }

    if (isLoadingBom) return { kind: "loading" };
    if (isBomError || !bom) return { kind: "no-bom" };
    return { kind: "ready", bom };
  })();

  return { status, bomArchRestrictions };
}
